import React, { useState } from 'react';
import { validatePasswordStrength, getPasswordStrengthScore } from '../../lib/passwordSecurity';
import { SensitiveChangeModal } from './SensitiveChangeModal';

interface SecurePasswordChangeFormProps {
  userId: number;
  userEmail: string;
  currentUsername: string;
  onPasswordChanged?: () => void;
  onClose?: () => void;
}

export const SecurePasswordChangeForm: React.FC<SecurePasswordChangeFormProps> = ({
  userId: _userId,
  userEmail,
  currentUsername,
  onPasswordChanged,
  onClose
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    label: '',
    color: ''
  });

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    
    // Update password strength
    const score = getPasswordStrengthScore(value);
    setPasswordStrength({
      score,
      label: score >= 80 ? 'Strong' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Weak',
      color: score >= 80 ? 'green' : score >= 60 ? 'yellow' : score >= 40 ? 'orange' : 'red'
    });

    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleOpenVerification = () => {
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    const validation = validatePasswordStrength(newPassword, currentUsername);
    if (!validation.valid) {
      setError(validation.errors[0]);
      return;
    }

    setError('');
    setShowVerifyModal(true);
  };

  const handleVerificationSuccess = () => {
    setShowVerifyModal(false);
    setSuccess(true);
    if (onPasswordChanged) onPasswordChanged();
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength.color) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'orange': return 'bg-orange-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (success) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <div className="text-center">
          <div className="bg-green-900/50 border border-green-500 text-green-400 px-4 py-3 rounded-lg mb-4">
            ✅ Password changed successfully!
          </div>
          <p className="text-white">Your password has been updated securely.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Change Password</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-4">
          ❌ {error}
        </div>
      )}

      <div className="space-y-4">
        <p className="text-white text-sm">
          Enter your new password. You'll be asked to re-authenticate with your current password, email, and username, then confirm a 6-digit code sent to your registered email.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => handlePasswordChange(e.target.value)}
              className="w-full px-4 py-3 pr-12 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
              placeholder="Enter your new password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {newPassword && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-white">Password Strength</span>
                <span className={`font-medium ${
                  passwordStrength.color === 'green' ? 'text-green-400' :
                  passwordStrength.color === 'yellow' ? 'text-yellow-400' :
                  passwordStrength.color === 'orange' ? 'text-orange-400' :
                  'text-red-400'
                }`}>
                  {passwordStrength.label}
                </span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                  style={{ width: `${passwordStrength.score}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleOpenVerification}
          disabled={!newPassword || passwordStrength.score < 60}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
        >
          Continue to Verification
        </button>
      </div>

      {showVerifyModal && (
        <SensitiveChangeModal
          changeType="password"
          newValue={newPassword}
          currentEmail={userEmail}
          currentUsername={currentUsername}
          onSuccess={handleVerificationSuccess}
          onClose={() => setShowVerifyModal(false)}
        />
      )}
    </div>
  );
};
